import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { fonts } from '@theme/fontsCustomer';
import KTDSLogoSrc from '@images/Ktds-dark.png';
import logoSrc from '@images/logo_190x32.png';
import MZCLogoSrc from '@images/mzcloud-dark.png';
import ZENIXLogoSrc from '@images/zenithcloud-dark.png';
import { dateFormat } from '@utils/dateUtil';
import type { SubscriptionBillingModel } from '@models/SubscriptionBillingModel';
import type { SubscriptionModel } from '@models/SubscriptionModel';
import type { MetricUsageCostModel } from '@repositories/billingOptimizationRepository/models/MetricUsageCostModel';
import { SubscriptionBillingDetailView } from './layout/SubscriptionBillingDetailView';

type Props = {
  subscription: SubscriptionModel;
  subscriptionBilling: SubscriptionBillingModel;
  metricUsageCosts: MetricUsageCostModel[];
};

export const SubscriptionBillingPrintView = forwardRef(
  ({ subscription, subscriptionBilling, metricUsageCosts }: Props, ref: React.ForwardedRef<HTMLDivElement>) => {
    const { t } = useTranslation();
    const { COMPANY_ID } = getEnvConfig();

    const getLogoSrc = () => {
      switch (COMPANY_ID) {
        case 'KT_DS':
          return KTDSLogoSrc;
        case 'MEGAZONE':
          return MZCLogoSrc;
        case 'ZENITH':
          return ZENIXLogoSrc;
        default:
          return logoSrc;
      }
    };

    return createPortal(
      <Container ref={ref}>
        <Header>
          <TitleWrapper>
            <Month>{dateFormat(subscriptionBilling.month, '', 'YYYY.MM')}</Month>
            <Title>{t('Acc_Detail_18')}</Title>
          </TitleWrapper>
        </Header>
        <Logo src={getLogoSrc()} alt="logo" $width={380} $height={64} $company={COMPANY_ID} />
        <Divider />
        <SubscriptionBillingDetailView
          subscription={subscription}
          subscriptionBilling={subscriptionBilling}
          metricUsageCosts={metricUsageCosts}
        />
      </Container>,
      document.body,
    );
  },
);

const Container = styled.div`
  position: absolute;
  z-index: -1;
  visibility: hidden;

  @media print {
    z-index: 0;
    visibility: visible;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 52px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Month = styled.div`
  ${fonts.Headline7}
  margin: 0px 0px 4px 4px;
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
`;

const Title = styled.div`
  ${fonts.Headline3}
`;

const Logo = styled.img<{ $width: number; $height: number; $company: string }>`
  position: absolute;
  top: 20px;
  right: -115px;
  right: ${({ $company }) => ($company === 'KT_DS' ? '-85px' : '-115px')};
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
`;

const Divider = styled.hr`
  border: 2px solid ${({ theme: { colors } }) => colors['border-gray-darker']};
  margin-bottom: 48px;
`;
